import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import React from 'react';

import type { Agent } from '../data/types';

interface ReassignPopoverProps {
  agents: Agent[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The popover trigger (button/icon). Must accept ref-forwarding via Radix's `asChild`. */
  children: React.ReactNode;
  onPick: (agentId: string) => void;
  align?: 'start' | 'center' | 'end';
}

/**
 * Single source of truth for the reassign UI used by both `CaseCard` (per-case
 * icon trigger) and `AggregateRowList` (bulk-reassign dropdown trigger).
 *
 * The trigger is passed in via `children` so each caller controls the look of
 * the button while the popover body — searchable agent list with avatar
 * initials — stays identical.
 */
export function ReassignPopover({
  agents,
  open,
  onOpenChange,
  children,
  onPick,
  align = 'end',
}: ReassignPopoverProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className="p-0 w-56"
        align={align}
        onWheel={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Reassign to..." className="h-9" />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>No agents found.</CommandEmpty>
            <CommandGroup>
              {agents.map(agent => (
                <CommandItem
                  key={agent.id}
                  value={agent.name}
                  onSelect={() => onPick(agent.id)}
                  className="cursor-pointer"
                >
                  <span className="inline-flex size-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground mr-2 shrink-0">
                    {agent.initial}
                  </span>
                  <span className="truncate text-sm">{agent.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
